import { Injectable } from '@angular/core';

/** Visual avatar category — drives the coloured tile behind each row in the
 *  slider. The full `/notifications` page uses its own richer templates, so
 *  this field only affects the slider. */
export type NotifCategory = 'alert' | 'ai' | 'success' | 'system';

/** Toaster colour. Matches `ToasterConfig['type']`. */
export type NotifSeverity = 'success' | 'info' | 'warning' | 'error';

/** Output of the service. No buttons or handlers, just strings. `description`
 *  is HTML-safe (entities already escaped) so it can be bound via
 *  `[innerHTML]`. */
export interface NotifContent {
  title: string;
  description: string;
  category: NotifCategory;
  /** Toaster colour for this notifyType. */
  severity: NotifSeverity;
  /** Registered @ng-icons Tabler name (see icon-provider.ts). */
  icon: string;
  /** Optional avatar URL — either a business logo or user photo when the
   *  backend supplied one. Slider can fall back to the category icon. */
  avatarUrl?: string;
}

/** Pure, framework-free formatter that converts a raw backend notification
 *  record into the small `NotifContent` shape. Consumed by the notification
 *  toaster (langding-page-nav-bar `showNotificationToaster`). One entry in
 *  `SPECS` per `notifyType` — add new types there. */
@Injectable({ providedIn: 'root' })
export class NotificationContentService {
  format(n: any): NotifContent {
    const rawType = String(n?.notifyType || '').trim();
    // Normalise hyphenated / spaced variants (`INTERVIEW-SCHEDULED`,
    // `CANDIDATE BACKOUT`) and casing so one SPECS key covers every form.
    const type = rawType.toUpperCase().replace(/[-\s]+/g, '_');

    const spec = SPECS[type];
    const f = resolveFields(n);
    const title = spec ? spec.title : fallbackTitle(type);
    const description = (spec && spec.message(f)) || fallbackDescription(f);
    const look = spec ? { severity: spec.severity, icon: spec.icon } : fallbackLook(type);

    return {
      title,
      description,
      category: categoryFor(type),
      severity: look.severity,
      icon: look.icon,
      avatarUrl: pickAvatarUrl(n),
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — kept private to this file.
// ─────────────────────────────────────────────────────────────────────────────

/** HTML-escape raw user-supplied strings. */
function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Return true when `s` looks like a backend identifier / token / numeric
 *  fragment rather than human copy (UUIDs, `JID100100122`, `1`, `#123`,
 *  strings shorter than 3 chars). The `message` column often holds these. */
function looksLikeId(s: string | null | undefined): boolean {
  const v = (s || '').trim();
  if (!v) return true;
  if (UUID_RE.test(v)) return true;
  if (/^[A-Z]{2,5}[\-_]?\d{2,}$/i.test(v)) return true;  // JID100100122, JOB_42
  if (/^#?\d+$/.test(v)) return true;                     // 1, 42, #123
  if (v.length < 3) return true;                          // "OK", "X", "1"
  return false;
}

function humanizeType(type: string | null | undefined): string {
  if (!type) return 'Notification';
  return String(type)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/** Trimmed string, with `null` / `undefined` (real or stringified) as ''. */
function clean(v: any): string {
  if (v == null) return '';
  const s = String(v).trim();
  return s === 'null' || s === 'undefined' ? '' : s;
}

/** Job titles arrive URI-encoded (`Senior%20Java%20Developer`). Same
 *  semantics as DecodeUriPipe: return the input when it is malformed. */
function decode(v: any): string {
  const s = clean(v);
  if (!s) return s;
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
}

function fullName(first: any, last: any): string {
  return [clean(first), clean(last)].filter(Boolean).join(' ');
}

/** `<strong>` around an escaped value, or '' when there is no value. */
function b(v: string): string {
  return v ? `<strong>${esc(v)}</strong>` : '';
}

/** `" by <strong>Acme</strong>"`, or '' when there is no value. */
function by(v: string): string {
  return v ? ` by ${b(v)}` : '';
}

/** `ON_HOLD` / `ACTIVE` → `On Hold` / `Active`. */
function prettyStatus(v: string): string {
  return v.replace(/[_-]+/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).trim();
}

/** Text after the first `": "` in backend prose (`"Document expired: PAN Card"`). */
function afterColon(v: string): string {
  const i = v.indexOf(': ');
  return i >= 0 ? v.slice(i + 2).trim() : '';
}

function isNumber(v: string): boolean {
  return /^\d+(\.\d+)?$/.test(v);
}

function plural(count: number | string, one: string, many: string): string {
  return Number(count) === 1 ? one : many;
}

/** Comma-joined id list in `message` → number of ids. */
function idCount(v: string): number {
  return v.split(',').filter(s => s.trim()).length;
}

/** MM/dd/yyyy — the format the /notifications page prints for Stripe dates. */
function formatDate(v: any): string {
  if (v == null || v === '') return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (x: number) => (x < 10 ? '0' + x : String(x));
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

/** Every value a message can use, read from the field that actually holds it. */
interface Fields {
  n: any;
  d: any;
  raw: string;
  entity: string;
  entityId: string;
  /** The user who acted (sender). */
  actor: string;
  /** The candidate the notification is about — NOT the sender. */
  candidate: string;
  candidateId: string;
  jobTitle: string;
  jobId: string;
  business: string;
  supplier: string;
  customer: string;
  document: string;
  invoiceId: string;
  reason: string;
  workOrderId: string;
  revision: string;
  newStatus: string;
  community: string;
  network: string;
  team: string;
  group: string;
  organisation: string;
}

function resolveFields(n: any): Fields {
  const d = n?.notifyData && typeof n.notifyData === 'object' ? n.notifyData : {};
  const cd = d.candidateData && typeof d.candidateData === 'object' ? d.candidateData : {};
  const raw = clean(n?.message);
  return {
    n,
    d,
    raw,
    entity: clean(n?.notifyEntity),
    entityId: clean(n?.notifyEntityId),
    actor: fullName(d.firstName, d.lastName),
    candidate: fullName(cd.firstName, cd.lastName) || clean(d.candidateName),
    candidateId: clean(cd.candidateId) || clean(d.candidateId),
    // Never `notifyData.title` — that is the sender's profile title, not the job.
    jobTitle: decode(d.jobData?.jobTitle) || decode(d.jobTitle),
    jobId: clean(d.jobId),
    business: clean(d.businessName),
    supplier: clean(d.supplierBusinessName),
    customer: clean(d.customerBusinessName),
    document: clean(d.documentName) || clean(d.documentType) || clean(d.docType),
    invoiceId: clean(d.invoiceId),
    reason: clean(d.rejectionReason),
    workOrderId: clean(d.workOrderId) || ((raw.match(/WOID\d+/) || [])[0] || ''),
    revision: clean(d.revisionNumber) || ((raw.match(/Rev #(\d+)/) || [])[1] || ''),
    newStatus: clean(d.newStatus) || ((raw.match(/status changed to (.+)$/i) || [])[1] || '').trim(),
    community: clean(d.communityName),
    network: clean(d.networkName),
    team: clean(d.teamName),
    group: clean(d.messageGroupName),
    organisation: clean(d.organisation),
  };
}

// ── Reference helpers used by several messages ──────────────────────────────

/** The job: its title, else "job JID…", else ''. */
function jobRef(f: Fields): string {
  if (f.jobTitle) return b(f.jobTitle);
  if (f.jobId) return `job ${b(f.jobId)}`;
  return '';
}

function forJob(f: Fields): string {
  const ref = jobRef(f);
  return ref ? ` for ${ref}` : '';
}

/** Sentence-start candidate reference. */
function candidateRef(f: Fields): string {
  return f.candidate ? b(f.candidate) : 'A candidate';
}

/** Mid-sentence candidate reference. */
function candidateObj(f: Fields): string {
  return f.candidate ? b(f.candidate) : 'a candidate';
}

function invoiceRef(f: Fields): string {
  return f.invoiceId ? `Invoice ${b(f.invoiceId)}` : 'An invoice';
}

function forCandidate(f: Fields): string {
  return f.candidate ? ` for ${b(f.candidate)}` : '';
}

function workOrderRef(f: Fields): string {
  return f.workOrderId ? `Work order ${b(f.workOrderId)}` : 'A work order';
}

/** "was " when the subject is a generic placeholder ("An invoice was
 *  rejected"); named subjects keep the shorter headline form
 *  ("Invoice INV-1 rejected"), which matters on a one-line toast. */
function wasIf(generic: boolean): string {
  return generic ? 'was ' : '';
}

function reasonSuffix(reason: string): string {
  return reason ? ` Reason: ${esc(reason)}.` : '';
}

/** Candidate-document types keep the document name in `message`
 *  (`AADHAAR_CARD`), shown title-cased on the page. */
function documentFromMessage(f: Fields): string {
  if (f.document) return f.document;
  if (!f.raw || UUID_RE.test(f.raw)) return '';
  return /_/.test(f.raw) || f.raw === f.raw.toUpperCase() ? prettyStatus(f.raw) : f.raw;
}

/** "post" / "job" / "candidate" for moderation copy. */
function reportedItem(f: Fields): string {
  if (f.d.postContentType) return 'post';
  const e = f.entity.toLowerCase();
  return e && !looksLikeId(e) ? e : 'item';
}

function pageName(v: string, fallback: string): string {
  return v ? b(v) : fallback;
}

function humanMessage(f: Fields): string {
  return f.raw && !looksLikeId(f.raw) ? esc(f.raw) : '';
}

function suggestedCandidate(f: Fields): string {
  if (f.candidate) return b(f.candidate);
  if (f.candidateId) return `candidate ${b(f.candidateId)}`;
  return 'your candidate';
}

function planName(v: string): string {
  return v && !looksLikeId(v) ? `${b(v)} plan` : 'plan';
}

function onDate(f: Fields): string {
  const date = formatDate(f.n?.notifyTime);
  return date ? ` on ${b(date)}` : '';
}

function resumeRequested(f: Fields): string {
  if (f.candidate) return `Resume requested for ${b(f.candidate)}${by(f.actor)}.`;
  if (f.actor) return `${b(f.actor)} requested a resume.`;
  return 'You have a new resume request.';
}

function resumeUploaded(f: Fields): string {
  if (f.candidate) return `Resume for ${b(f.candidate)} uploaded${by(f.actor)}.`;
  return 'The resume you requested has been uploaded.';
}

function reportActioned(f: Fields): string {
  const item = reportedItem(f);
  return f.raw.toUpperCase() === 'BLOCKED'
    ? `The ${item} you reported was blocked by the admin.`
    : `The admin took action on the ${item} you reported.`;
}

/** "3 jobs and 2 candidates" from two counts; zero / missing parts dropped. */
function listingCounts(jobs: any, candidates: any): string {
  const parts: string[] = [];
  const j = clean(jobs);
  const c = clean(candidates);
  if (isNumber(j) && Number(j) > 0) parts.push(`${b(j)} ${plural(j, 'job', 'jobs')}`);
  if (isNumber(c) && Number(c) > 0) parts.push(`${b(c)} ${plural(c, 'candidate', 'candidates')}`);
  return parts.join(' and ');
}

function actorOr(f: Fields, fallback: string): string {
  return f.actor ? b(f.actor) : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-type copy. Title ≤30 chars. The toaster shows the message on ONE line
// (cut with an ellipsis), so each message leads with its subject — the
// document / invoice / candidate / job — and puts the actor after it.
// ─────────────────────────────────────────────────────────────────────────────

interface Spec {
  title: string;
  severity: NotifSeverity;
  icon: string;
  message: (f: Fields) => string;
}

function spec(title: string, severity: NotifSeverity, icon: string, message: (f: Fields) => string): Spec {
  return { title, severity, icon, message };
}

const SPECS: { [type: string]: Spec } = {
  // ── Contract management — agreement
  CONTRACT_AGREEMENT_CHECKLIST_ASSIGNED: spec('Documents required', 'warning', 'tablerClipboardList', f =>
    `Agreement checklist assigned${by(f.business)}. Upload the required documents.`),
  CONTRACT_AGREEMENT_DOCUMENT_UPLOADED: spec('Agreement uploaded', 'info', 'tablerFileUpload', f =>
    `${f.document ? b(f.document) : 'An agreement document was'} uploaded${by(f.business)} for your review.`),
  CONTRACT_AGREEMENT_SIGNING_REQUESTED: spec('Signature needed', 'warning', 'tablerWritingSign', f =>
    f.document
      ? `Sign ${b(f.document)}${f.business ? ` — requested by ${b(f.business)}` : ''}.`
      : `Your signature is requested${by(f.business)}.`),
  CONTRACT_AGREEMENT_SIGNED: spec('Agreement signed', 'success', 'tablerWritingSign', f =>
    `${f.document ? b(f.document) : 'An agreement was'} signed${by(clean(f.d.signedBy) || f.business)}.`),
  CONTRACT_AGREEMENT_ACCEPTED: spec('Agreement accepted', 'success', 'tablerCircleCheck', f =>
    `${f.document ? b(f.document) : 'Your agreement was'} accepted${by(f.business)}.`),
  CONTRACT_AGREEMENT_REJECTED: spec('Agreement rejected', 'error', 'tablerCircleX', f =>
    `${f.document ? b(f.document) : 'Your agreement was'} rejected${by(f.business)}.${reasonSuffix(f.reason)}`),

  // ── Contract management — company documents
  CONTRACT_COMPANY_DOC_CHECKLIST_CREATED: spec('Document requested', 'warning', 'tablerFileUpload', f =>
    f.document
      ? `Upload ${b(f.document)}${f.business ? ` — requested by ${b(f.business)}` : ''}.`
      : `A company document was requested${by(f.business)}.`),
  CONTRACT_COMPANY_DOC_UPLOADED: spec('Document uploaded', 'info', 'tablerFileUpload', f =>
    `${f.document ? b(f.document) : 'A company document was'} uploaded${by(f.business)} for your review.`),
  CONTRACT_COMPANY_DOC_VERIFIED: spec('Document verified', 'success', 'tablerFileCheck', f =>
    `${f.document ? b(f.document) : 'Your company document was'} verified${by(f.business)}.`),
  CONTRACT_COMPANY_DOC_REJECTED: spec('Document rejected', 'error', 'tablerCircleX', f =>
    `${f.document ? b(f.document) : 'Your company document was'} rejected${by(f.business)}.${reasonSuffix(f.reason)}`),
  CONTRACT_DOCUMENT_EXPIRED: spec('Document expired', 'warning', 'tablerAlertTriangle', f => {
    const doc = f.document || afterColon(f.raw);
    return doc ? `${b(doc)} has expired. Upload a renewed copy.` : 'A contract document has expired. Upload a renewed copy.';
  }),
  CONTRACT_COMPANY_DOC_VAULT: spec('New vault document', 'info', 'tablerFolder', f => {
    const doc = f.document || afterColon(f.raw);
    return `${doc ? b(doc) : 'A new document was'} added to the company document vault.`;
  }),
  CONTRACT_TEMPLATE_VAULT: spec('New template added', 'info', 'tablerFolder', f => {
    const doc = f.document || afterColon(f.raw);
    return `${doc ? b(doc) : 'A new template was'} added to the template vault.`;
  }),

  // ── Contract management — contract, work order, candidate
  CONTRACT_INITIATED: spec('Contract initiated', 'info', 'tablerFileDescription', f =>
    `Contract${forCandidate(f)} started${by(f.business)}. Agreements are pending.`),
  CONTRACT_WORK_ORDER_INITIATED: spec('New work order', 'info', 'tablerClipboardList', f =>
    `${workOrderRef(f)}${forCandidate(f)} received${f.business ? ` from ${b(f.business)}` : ''}. Please review.`),
  CONTRACT_WORK_ORDER_ACCEPTED: spec('Work order accepted', 'success', 'tablerCircleCheck', f =>
    `${workOrderRef(f)}${forCandidate(f)} ${wasIf(!f.workOrderId)}accepted${by(f.business)}.`),
  CONTRACT_WORK_ORDER_REJECTED: spec('Work order rejected', 'error', 'tablerCircleX', f =>
    `${workOrderRef(f)}${forCandidate(f)} ${wasIf(!f.workOrderId)}rejected${by(f.business)}.`),
  CONTRACT_WORK_ORDER_REVISED: spec('Work order revised', 'warning', 'tablerClipboardList', f =>
    `${workOrderRef(f)}${f.revision ? ` (Rev #${esc(f.revision)})` : ''} ${wasIf(!f.workOrderId)}revised${by(f.business)}. Review the new terms.`),
  CONTRACT_WORK_ORDER_VOIDED: spec('Work order voided', 'warning', 'tablerBan', f =>
    `${workOrderRef(f)}${forCandidate(f)} ${wasIf(!f.workOrderId)}voided${by(f.business)}.`),
  CONTRACT_CANDIDATE_STATUS_CHANGED: spec('Candidate status updated', 'info', 'tablerUserCheck', f => {
    const candidate = f.candidate || ((f.raw.match(/^(.+?) status changed to /i) || [])[1] || '').trim();
    if (candidate && f.newStatus) return `${b(candidate)} moved to ${b(f.newStatus)}.`;
    if (candidate) return `${b(candidate)}'s status was updated.`;
    return "A candidate's contract status was updated.";
  }),

  // ── Contract management — invoice & payment
  CONTRACT_INVOICE_SUBMITTED: spec('Invoice submitted', 'info', 'tablerFileInvoice', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}submitted${by(f.supplier || f.business)}. Please review.`),
  CONTRACT_INVOICE_UPDATED: spec('Invoice updated', 'info', 'tablerFileInvoice', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}updated${by(f.supplier || f.business)}. Review before approving.`),
  CONTRACT_INVOICE_APPROVED: spec('Invoice approved', 'success', 'tablerFileInvoice', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}approved${by(f.customer || f.business)}.`),
  CONTRACT_INVOICE_REJECTED: spec('Invoice rejected', 'error', 'tablerFileInvoice', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}rejected${by(f.customer || f.business)}.${reasonSuffix(f.reason)}`),
  CONTRACT_INVOICE_PAID: spec('Invoice paid', 'success', 'tablerCash', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}marked as paid${by(f.customer || f.business)}.`),
  CONTRACT_PAYMENT_RECEIVED: spec('Payment received', 'success', 'tablerCash', f =>
    `Payment for ${f.invoiceId ? `invoice ${b(f.invoiceId)}` : 'an invoice'} confirmed${by(f.supplier || f.business)}.`),
  CONTRACT_INVOICE_VOIDED: spec('Invoice voided', 'info', 'tablerBan', f =>
    `${invoiceRef(f)}${forCandidate(f)} ${wasIf(!f.invoiceId)}voided. No action needed.`),

  // ── Contract management — credit notes, disputes, settings. The backend
  // `message` is already a complete sentence for these and nothing parses it
  // into notifyData, so it is shown as-is.
  CONTRACT_CREDIT_CREATED: spec('Credit note created', 'info', 'tablerFileInvoice', f =>
    humanMessage(f) || 'A credit note was created.'),
  CONTRACT_CREDIT_APPLIED: spec('Credit note applied', 'success', 'tablerFileInvoice', f =>
    humanMessage(f) || 'A credit note was applied.'),
  CONTRACT_CREDIT_VOIDED: spec('Credit note voided', 'info', 'tablerBan', f =>
    humanMessage(f) || 'A credit note was voided.'),
  CONTRACT_DISPUTE_CREATED: spec('Dispute opened', 'warning', 'tablerAlertTriangle', f =>
    humanMessage(f) || 'A dispute was opened on an invoice.'),
  CONTRACT_DISPUTE_COMMENT: spec('New dispute comment', 'info', 'tablerMessage', f =>
    humanMessage(f) || 'A new comment was added to a dispute.'),
  CONTRACT_DISPUTE_STATUS: spec('Dispute updated', 'info', 'tablerAlertTriangle', f =>
    humanMessage(f) || 'A dispute status was updated.'),
  CONTRACT_SETTINGS_CHANGED: spec('Contract settings changed', 'info', 'tablerSettings', f =>
    humanMessage(f) || 'Contract settings were updated.'),

  // ── Job management
  JOB_STATUS_CHANGED: spec('Job status changed', 'info', 'tablerBriefcase', f => {
    const status = f.raw && !looksLikeId(f.raw) ? prettyStatus(f.raw) : '';
    const job = jobRef(f);
    if (job && status) return `${job} moved to ${b(status)}.`;
    if (status) return `A job moved to ${b(status)}.`;
    if (job) return `${job} status was updated.`;
    return 'A job status was updated.';
  }),
  JOB_TARGET_TRANSFER: spec('Jobs assigned to you', 'info', 'tablerArrowsExchange', f => {
    const count = idCount(f.raw);
    return count
      ? `${count} ${plural(count, 'job was', 'jobs were')} assigned to you by the Super Admin.`
      : 'Jobs were assigned to you by the Super Admin.';
  }),
  JOB_SOURCE_TRANSFER: spec('Jobs reassigned', 'info', 'tablerArrowsExchange', f => {
    const count = idCount(f.raw);
    return count
      ? `${count} of your ${plural(count, 'jobs was', 'jobs were')} reassigned to another user.`
      : 'Your jobs were reassigned to another user.';
  }),
  CANDIDATE_TARGET_TRANSFER: spec('Candidates assigned to you', 'info', 'tablerArrowsExchange', f => {
    const count = idCount(f.raw);
    return count
      ? `${count} ${plural(count, 'candidate was', 'candidates were')} assigned to you by the Super Admin.`
      : 'Candidates were assigned to you by the Super Admin.';
  }),
  CANDIDATE_SOURCE_TRANSFER: spec('Candidates reassigned', 'info', 'tablerArrowsExchange', f => {
    const count = idCount(f.raw);
    return count
      ? `${count} of your ${plural(count, 'candidates was', 'candidates were')} reassigned to another user.`
      : 'Your candidates were reassigned to another user.';
  }),
  JOB_OWNER_REQUEST: spec('Documents requested', 'warning', 'tablerFileUpload', f =>
    `Upload mandatory documents${forJob(f)} to continue.`),
  JOB_OWNER_ADDITIONAL_REQUEST: spec('More documents needed', 'warning', 'tablerFileUpload', f =>
    `Additional documents requested${forJob(f)}. Please upload them.`),

  // ── Job management — candidate documents
  CANDIDATE_OWNER_SUBMISSION: spec('Documents submitted', 'info', 'tablerFileCheck', f =>
    `Requested documents submitted${forJob(f)}. Please verify.`),
  CANDIDATE_DOCUMENT_REUPLOAD: spec('Document re-uploaded', 'info', 'tablerFileUpload', f => {
    const doc = documentFromMessage(f);
    return `${doc ? b(doc) : 'A rejected document was'} re-uploaded${forJob(f)}. Please verify.`;
  }),
  CANDIDATE_EXPIRED_DOCUMENT_REUPLOAD: spec('Expired document replaced', 'info', 'tablerFileUpload', f => {
    const doc = documentFromMessage(f);
    return `${doc ? b(doc) : 'An expired document was'} re-uploaded${forJob(f)}. Please verify.`;
  }),
  CANDIDATE_DOCUMENT_EXPIRED: spec('Document expired', 'warning', 'tablerAlertTriangle', f =>
    `A document${forJob(f)} has expired. Please re-upload it.`),
  CANDIDATE_DOCUMENT_REJECTION: spec('Document rejected', 'error', 'tablerCircleX', f => {
    const doc = documentFromMessage(f);
    // The page shows notifyEntityId as the rejection reason for this type.
    const reason = f.reason || (f.entityId && !looksLikeId(f.entityId) ? f.entityId : '');
    return `${doc ? b(doc) : 'A document was'} rejected${forJob(f)}.${reasonSuffix(reason)}`;
  }),
  CANDIDATE_DOCUMENT_ALL_VERIFIED: spec('All documents verified', 'success', 'tablerFileCheck', f =>
    `All documents${forJob(f)} are verified.`),

  // ── Candidate pipeline. The candidate is notifyData.candidateData; the
  // firstName / lastName at the top of notifyData is the sender.
  CANDIDATE_INVITATION_ACCEPTED: spec('Invitation accepted', 'success', 'tablerUserCheck', f =>
    f.actor
      ? `${b(f.actor)} accepted your invite${forJob(f)}.`
      : `Your job invitation${forJob(f)} was accepted.`),
  CANDIDATE_INVITATION_REJECTED: spec('Invitation declined', 'error', 'tablerCircleX', f =>
    f.actor
      ? `${b(f.actor)} declined your invite${forJob(f)}.`
      : `Your job invitation${forJob(f)} was declined.`),
  JOB_APPLIED: spec('Application received', 'info', 'tablerUserPlus', f =>
    `${candidateRef(f)} applied${forJob(f)}.`),
  CANDIDATE_SHORTLISTED: spec('Candidate shortlisted', 'success', 'tablerUserCheck', f =>
    `${candidateRef(f)} was shortlisted${forJob(f)}.`),
  INTERVIEW_SCHEDULED: spec('Interview scheduled', 'info', 'tablerCalendar', f => {
    const ref = jobRef(f);
    return `Interview scheduled for ${candidateObj(f)}${ref ? ` — ${ref}` : ''}.`;
  }),
  INTERVIEW_ACCEPTED: spec('Interview accepted', 'success', 'tablerCalendar', f =>
    `${candidateRef(f)} accepted the interview${forJob(f)}.`),
  INTERVIEW_REJECTED: spec('Interview declined', 'error', 'tablerCalendar', f =>
    `${candidateRef(f)} declined the interview${forJob(f)}.`),
  INTERVIEW_CONFIRMATION_REQUIRED: spec('Confirm interview', 'warning', 'tablerCalendar', f =>
    `Interview for ${candidateObj(f)}${forJob(f)} needs your confirmation.`),
  INTERVIEW_CANCELLED: spec('Interview cancelled', 'error', 'tablerCalendar', f =>
    `Interview for ${candidateObj(f)}${forJob(f)} was cancelled.`),
  JOB_OFFERED: spec('Offer sent', 'success', 'tablerBriefcase', f =>
    `Offer sent to ${candidateObj(f)}${forJob(f)}.`),
  OFFER_ACCEPTED: spec('Offer accepted', 'success', 'tablerCircleCheck', f =>
    `${candidateRef(f)} accepted the offer${forJob(f)}.`),
  OFFER_DECLINED: spec('Offer declined', 'error', 'tablerCircleX', f =>
    `${candidateRef(f)} declined the offer${forJob(f)}.`),
  // What CandidateService actually sends for the OFFER-ACCEPTED / OFFER-DECLINED statuses.
  JOB_OFFER_ACCEPTED: spec('Offer accepted', 'success', 'tablerCircleCheck', f =>
    `${candidateRef(f)} accepted the offer${forJob(f)}.`),
  JOB_OFFER_DECLINED: spec('Offer declined', 'error', 'tablerCircleX', f =>
    `${candidateRef(f)} declined the offer${forJob(f)}.`),
  OFFER_WITHDRAWN: spec('Offer withdrawn', 'warning', 'tablerBan', f =>
    `Offer to ${candidateObj(f)}${forJob(f)} was withdrawn.`),
  OFFER_EXPIRING: spec('Offer expiring soon', 'warning', 'tablerHourglass', f =>
    `Offer to ${candidateObj(f)}${forJob(f)} is expiring soon.`),
  PRE_ONBOARDED: spec('Pre-onboarding started', 'success', 'tablerUserCheck', f =>
    `${candidateRef(f)} moved to pre-onboarding${forJob(f)}.`),
  CANDIDATE_PREONBOARDED: spec('Pre-onboarding started', 'success', 'tablerUserCheck', f =>
    `${candidateRef(f)} moved to pre-onboarding${forJob(f)}.`),
  CANDIDATE_ONBOARDED: spec('Candidate onboarded', 'success', 'tablerUserCheck', f =>
    `${candidateRef(f)} was onboarded${forJob(f)}.`),
  CANDIDATE_WITHDRAWN: spec('Candidate withdrawn', 'warning', 'tablerCircleMinus', f => {
    const ref = jobRef(f);
    return `${candidateRef(f)} was withdrawn${ref ? ` from ${ref}` : ''}.`;
  }),
  CANDIDATE_BACKOUT: spec('Candidate backed out', 'warning', 'tablerCircleMinus', f => {
    const ref = jobRef(f);
    return `${candidateRef(f)} backed out${ref ? ` of ${ref}` : ''}.`;
  }),
  CANDIDATE_REJECTED: spec('Candidate rejected', 'error', 'tablerCircleX', f =>
    `${candidateRef(f)} was rejected${forJob(f)}.`),
  CANDIDATE_GOT_SELECTED: spec('Candidate selected', 'success', 'tablerCircleCheck', f =>
    `${candidateRef(f)} was selected${forJob(f)}.`),
  CANDIDATE_SELECTED: spec('Candidate selected', 'success', 'tablerCircleCheck', f =>
    `${candidateRef(f)} was selected${forJob(f)}.`),

  // ── AI matching — talent pool
  SUGGESTED_CANDIDATES_TO_JOB_OWNER: spec('Candidate matches found', 'info', 'tablerSparkles', f =>
    `New candidate matches for ${jobRef(f) || 'your job'}. Tap to review.`),
  SUGGESTED_JOBS_TO_CANDIDATE_OWNER: spec('Job matches found', 'info', 'tablerSparkles', f =>
    `New job matches for ${suggestedCandidate(f)}. Tap to review.`),
  SUGGESTED_CANDIDATE_REFRESH: spec('Matches refreshed', 'info', 'tablerSparkles', f =>
    `Candidate matches refreshed for ${jobRef(f) || 'your job'}.`),
  SUGGESTED_JOB_REFRESH: spec('Matches refreshed', 'info', 'tablerSparkles', f =>
    `Job matches refreshed for ${suggestedCandidate(f)}.`),
  JOB_APPLIED_TO_JOB_OWNER: spec('New application', 'info', 'tablerUserPlus', f =>
    `A candidate applied for ${jobRef(f) || 'your job'}. Review their match score.`),
  JOB_APPLIED_TO_CANDIDATE_OWNER: spec('Application submitted', 'success', 'tablerCircleCheck', f => {
    const ref = jobRef(f);
    return ref ? `Application for ${ref} submitted successfully.` : 'Your candidate was applied to a job.';
  }),
  PROMOTIONAL_BENEFITS_ADDED: spec('Promotional benefits added', 'success', 'tablerSparkles', f =>
    f.raw && !UUID_RE.test(f.raw)
      ? `Promotional benefits added, valid until ${b(f.raw)}.`
      : 'Promotional benefits were added to your account.'),

  // ── Business pages / employees
  BUSINESS_PAGE_ACTIVATED: spec('Business page activated', 'success', 'tablerBuilding', f =>
    `${pageName(f.business, 'Your business page')} has been activated.`),
  BUSINESS_PAGE_DEACTIVATED: spec('Business page deactivated', 'warning', 'tablerBuilding', f =>
    `${pageName(f.business, 'Your business page')} has been deactivated.`),
  BUSINESS_PAGE_BLOCKED: spec('Business page blocked', 'error', 'tablerBan', f =>
    `${pageName(f.business, 'Your business page')} was blocked by the platform. Contact support.`),
  BUSINESS_PAGE_UNBLOCKED: spec('Business page unblocked', 'success', 'tablerBuilding', f =>
    `${pageName(f.business, 'Your business page')} has been unblocked.`),
  BUSINESS_EMPLOYEE_ADDED: spec('New employee added', 'success', 'tablerUserPlus', f =>
    `${actorOr(f, 'A new employee')} added to ${pageName(f.business, 'your business')}.`),
  // The employee-request types are work-experience verification (see the
  // /notifications page copy), not join requests.
  BUSINESS_EMPLOYEE_INVITE_SENT: spec('Verification pending', 'info', 'tablerUserCheck', f =>
    `Your work experience${f.business ? ` at ${b(f.business)}` : ''} is awaiting verification.`),
  BUSINESS_EMPLOYEE_INVITE_REQUEST: spec('Verify work experience', 'info', 'tablerUserCheck', f =>
    f.actor ? `Verify ${b(f.actor)}'s work experience.` : 'A work experience is waiting for your verification.'),
  BUSINESS_EMPLOYEE_REQUEST_ACCEPTED: spec('Work experience verified', 'success', 'tablerCircleCheck', f =>
    `Your work experience${f.business ? ` at ${b(f.business)}` : ''} has been verified.`),
  BUSINESS_EMPLOYEE_REQUEST_REJECTED: spec('Verification failed', 'error', 'tablerCircleX', f =>
    `Your work experience verification${f.business ? ` at ${b(f.business)}` : ''} failed.`),
  BUSINESS_EMPLOYEE_REVOKE: spec('Removed from business', 'error', 'tablerCircleX', f =>
    `You were removed from ${pageName(f.business, 'the business')} by the admin.`),
  BUSINESS_FOLLOWER_ADDED: spec('New business follower', 'success', 'tablerUserPlus', f =>
    f.actor
      ? `${b(f.actor)} is now following ${pageName(f.business, 'your business')}.`
      : `You have a new follower on ${pageName(f.business, 'your business')}.`),
  BUSINESS_FOLLOWER_INVITE_REQUEST_ACCEPTED: spec('Follower invite accepted', 'success', 'tablerCircleCheck', f =>
    `${actorOr(f, 'Someone')} accepted your invite to follow ${pageName(f.business, 'your business')}.`),
  BUSINESS_FOLLOWER_INVITE_REQUEST_REJECTED: spec('Follower invite declined', 'error', 'tablerCircleX', f =>
    `${actorOr(f, 'Someone')} declined your invite to follow ${pageName(f.business, 'your business')}.`),
  BUSINESS_PAGE_FOLLOWER_INVITE_SENT: spec('Follower invite', 'info', 'tablerUserPlus', f =>
    `You're invited to follow ${pageName(f.business, 'a business page')}.`),
  BUSINESS_PAGE_CONTACT_INVITE: spec('Contact invite', 'info', 'tablerUserPlus', f =>
    `${pageName(f.business, 'A business')} invited you to be an individual contact.`),
  BUSINESS_COLLABRATION_INVITE: spec('Collaboration invite', 'info', 'tablerUserPlus', f =>
    `${pageName(f.business, 'A business')} invited your business as a B2B contact.`),
  BUSINESS_PAGE_CONTACT_INVITE_ACCEPTED: spec('Contact invite accepted', 'success', 'tablerCircleCheck', f =>
    `${actorOr(f, 'Your contact')} accepted your contact request.`),
  BUSINESS_COLLABRATION_INVITE_ACCEPTED: spec('Collaboration accepted', 'success', 'tablerCircleCheck', f =>
    `${pageName(f.business || f.actor, 'A business')} accepted your contact request.`),
  USER_LEFT_ORGANISATION: spec('User left organisation', 'info', 'tablerCircleMinus', f =>
    `${actorOr(f, 'A user')} left ${pageName(f.organisation, 'the organisation')}.`),
  DOMAIN_BUSINESS_UPDATE: spec('Business domain updated', 'info', 'tablerWorld', f =>
    f.business ? `Domain updated for ${b(f.business)}.` : 'Your business domain was updated.'),

  // ── Social
  CONNECT_REQUEST_RECEIVED: spec('New connection request', 'info', 'tablerUserPlus', f =>
    `${actorOr(f, 'Someone')} sent you a connection request.`),
  CONNECT_REQUEST_ACCEPTED: spec('Connection accepted', 'success', 'tablerUserCheck', f =>
    `${actorOr(f, 'Someone')} accepted your connection request.`),
  CONNECT_REQUEST_CANCELLED: spec('Connection request cancelled', 'info', 'tablerCircleMinus', f =>
    `${actorOr(f, 'Someone')} cancelled their connection request.`),
  CONNECTION_REMOVED: spec('Connection removed', 'warning', 'tablerCircleMinus', f =>
    `${actorOr(f, 'Someone')} removed the connection with you.`),
  POST_LIKED: spec('Your post was liked', 'success', 'tablerHeart', f =>
    `${actorOr(f, 'Someone')} liked your post.`),
  POST_COMMENTED: spec('New comment on your post', 'info', 'tablerMessage', f =>
    `${actorOr(f, 'Someone')} commented on your post.`),
  POST_SHARED: spec('Your post was shared', 'info', 'tablerShare', f =>
    `${actorOr(f, 'Someone')} shared your post.`),

  // ── Networks / workspaces
  // Network invites: the Network owner receives these when the invited member responds.
  NETWORK_CONNECT_REQUEST_ACCEPTED: spec('Invitation accepted', 'success', 'tablerUserCheck', f =>
    `${actorOr(f, 'Someone')} accepted your invite request.`),
  NETWORK_CONNECT_REQUEST_REJECTED: spec('Invitation declined', 'error', 'tablerCircleX', f =>
    `${actorOr(f, 'Someone')} declined your invite request.`),
  NETWORK_INVITE_SENT: spec('Network invite', 'info', 'tablerNetwork', f =>
    `Invite to ${pageName(f.network, 'a network')}${f.actor ? ` from ${b(f.actor)}` : ''}.`),
  NETWORK_INVITE_CANCELLED: spec('Network invite cancelled', 'info', 'tablerNetwork', f =>
    `Invite to ${pageName(f.network, 'a network')} was cancelled.`),
  NETWORK_ACTIVATED: spec('Network activated', 'success', 'tablerNetwork', f =>
    `${pageName(f.network, 'Your network')} is now active.`),
  NETWORK_DELETED: spec('Network deactivated', 'warning', 'tablerNetwork', f =>
    `${pageName(f.network, 'A network')} was deleted.`),
  NETWORK_DEACTIVATED: spec('Network deactivated', 'warning', 'tablerNetwork', f =>
    `${pageName(f.network, 'A network')} has been deactivated.`),
  NETWORK_REMOVED: spec('Removed from network', 'warning', 'tablerNetwork', f =>
    `You were removed from ${pageName(f.network, 'a network')}.`),
  USER_ITSELF_REMOVE_FROM_NETWORK: spec('Member left network', 'info', 'tablerNetwork', f =>
    `${actorOr(f, 'A member')} left ${pageName(f.network, 'your network')}.`),
  JOINED_NETWORK: spec('New network member', 'success', 'tablerNetwork', f =>
    `${actorOr(f, 'A new member')} joined ${pageName(f.network, 'your network')}.`),
  JOINED_TEAM: spec('Workspace invite received', 'success', 'tablerUsers', f =>
    `${actorOr(f, 'A new member')} joined ${pageName(f.team, 'your workspace')}.`),
  LEFT_TEAM: spec('Member left workspace', 'info', 'tablerUsers', f =>
    `${actorOr(f, 'A member')} left ${pageName(f.team, 'your workspace')}.`),
  USER_ITSELF_REMOVE_FROM_TEAMS: spec('Member left workspace', 'info', 'tablerUsers', f =>
    `${actorOr(f, 'A member')} left ${pageName(f.team, 'your workspace')}.`),
  LEFT_TEAM_BY_ORG_CHANGE: spec('Removed from workspace', 'warning', 'tablerUsers', f =>
    `${actorOr(f, 'A member')} was removed from ${pageName(f.team, 'the workspace')} after leaving the organisation.`),
  TEAM_ACTIVATED: spec('Workspace activated', 'success', 'tablerUsers', f =>
    `${pageName(f.team, 'Your workspace')} is now active.`),
  TEAM_DEACTIVATED: spec('Workspace deactivated', 'warning', 'tablerUsers', f =>
    `${pageName(f.team, 'A workspace')} has been deactivated.`),
  TEAM_INACTIVE: spec('Workspace inactivated', 'warning', 'tablerUsers', f =>
    `${pageName(f.team, 'A workspace')} was inactivated — its super admin left.`),
  SUPERADMIN_INACTIVE: spec('Workspace access removed', 'warning', 'tablerUsers', f =>
    `Your access to ${pageName(f.team, 'a workspace')} was inactivated by the super admin.`),
  TEAM_OWNER_UPDATE: spec('Workspace owner updated', 'info', 'tablerUsers', f =>
    `Owner updated for ${pageName(f.team, 'your workspace')}.`),
  TEAM_INVITE_CANCELLED: spec('Workspace invite cancelled', 'info', 'tablerUsers', f =>
    `Invite to ${pageName(f.team, 'a workspace')} was cancelled.`),
  TEAMS_REMOVED: spec('Removed from workspace', 'error', 'tablerUsers', f =>
    `You were removed from ${pageName(f.team || (looksLikeId(f.raw) ? '' : f.raw), 'a workspace')}.`),
  TEAM_CONNECT_REQUEST_ACCEPTED: spec('Workspace request accepted', 'success', 'tablerUsers', f =>
    `${actorOr(f, 'Someone')} accepted your request to join ${pageName(f.team, 'the workspace')}.`),
  TEAM_CONNECT_REQUEST_CANCELLED: spec('Workspace request cancelled', 'info', 'tablerUsers', f =>
    `Request to join ${pageName(f.team, 'a workspace')} was cancelled.`),

  // ── Community
  COMMUNITY_USER_JOINED: spec('Community member joined', 'success', 'tablerUsersGroup', f =>
    `${actorOr(f, 'A new member')} joined ${pageName(f.community, 'your community')}.`),
  COMMUNITY_MEMBER_JOINED: spec('Community member joined', 'success', 'tablerUsersGroup', f =>
    `${actorOr(f, 'A new member')} joined ${pageName(f.community, 'your community')}.`),
  COMMUNITY_USER_JOIN_REQUEST: spec('Community join request', 'info', 'tablerUsersGroup', f =>
    `${actorOr(f, 'Someone')} asked to join ${pageName(f.community, 'your community')}.`),
  COMMUNITY_USER_INVITE_REQUEST: spec('Community invite', 'info', 'tablerUsersGroup', f =>
    `You're invited to join ${pageName(f.community, 'a community')}.`),
  COMMUNITY_MEMBER_INVITE_REQUEST: spec('Community invite', 'info', 'tablerUsersGroup', f =>
    `You're invited to join ${pageName(f.community, 'a community')}.`),
  COMMUNITY_USER_INVITE_REQUEST_ACCEPTED: spec('Community invite accepted', 'success', 'tablerUsersGroup', f =>
    `${actorOr(f, 'Someone')} accepted your invite to ${pageName(f.community, 'your community')}.`),
  COMMUNITY_USER_INVITE_REQUEST_REJECTED: spec('Community invite declined', 'error', 'tablerUsersGroup', f =>
    `${actorOr(f, 'Someone')} declined your invite to ${pageName(f.community, 'your community')}.`),
  COMMUNITY_USER_REQUEST_ACCEPTED: spec('Community request accepted', 'success', 'tablerUsersGroup', f =>
    `Your request to join ${pageName(f.community, 'the community')} was accepted.`),
  COMMUNITY_USER_REQUEST_REJECTED: spec('Community request declined', 'error', 'tablerUsersGroup', f =>
    `Your request to join ${pageName(f.community, 'the community')} was declined.`),
  COMMUNITY_MEMBER_REQUEST_ACCEPTED: spec('Community request accepted', 'success', 'tablerUsersGroup', f =>
    `Your request to join ${pageName(f.community, 'the community')} was accepted.`),
  COMMUNITY_MEMBER_REQUEST_REJECTED: spec('Community request declined', 'error', 'tablerUsersGroup', f =>
    `Your request to join ${pageName(f.community, 'the community')} was declined.`),
  COMMUNITY_PAGE_ACTIVATED: spec('Community page activated', 'success', 'tablerUsersGroup', f =>
    `${pageName(f.community, 'Your community page')} is now active.`),
  COMMUNITY_PAGE_DEACTIVATED: spec('Community page deactivated', 'warning', 'tablerUsersGroup', f =>
    `${pageName(f.community, 'Your community page')} was deactivated.`),

  // ── Messaging groups
  ADDED_TO_MSG_GRP: spec('Added to a message group', 'info', 'tablerMessages', f =>
    `${actorOr(f, 'Someone')} added you to ${pageName(f.group, 'a message group')}.`),
  REMOVED_FROM_MSG_GRP: spec('Removed from message group', 'warning', 'tablerMessages', f =>
    `${actorOr(f, 'Someone')} removed you from ${pageName(f.group, 'a message group')}.`),
  LEFT_FROM_MSG_GRP: spec('Member left message group', 'info', 'tablerMessages', f =>
    `${actorOr(f, 'A member')} left ${pageName(f.group, 'the message group')}.`),

  // ── Credits & subscription
  MONTHLY_ADD_ON_CREDITS: spec('Monthly credits refreshed', 'success', 'tablerCoin', () =>
    'Your monthly credits are reset.'),
  MONTHLY_REFRESH_ALERT: spec('Monthly credits refreshed', 'success', 'tablerCoin', f =>
    f.raw && !UUID_RE.test(f.raw)
      ? `Your free credits are reset, valid until ${b(f.raw)}.`
      : 'Your monthly free credits have been reset.'),
  INSUFFICIENT_CREDITS_ALERT: spec('Not enough credits', 'warning', 'tablerAlertTriangle', f => {
    const need = clean(f.n?.creditsInNeed) || clean(f.d.creditsInNeed);
    return isNumber(need)
      ? `You need ${b(need)} more credits to keep listings active.`
      : 'Your credit balance is low. Purchase credits to stay active.';
  }),
  CREDITS_CONSUMPTION_ALERT: spec('Credits will be deducted', 'warning', 'tablerCoin', f => {
    const credits = clean(f.d.creditsForConsumption);
    return isNumber(credits)
      ? `${b(credits)} credits will be deducted for your active listings.`
      : 'Credits will be deducted for your active listings.';
  }),
  CREDIT_REWARDED: spec('Credits added', 'success', 'tablerCoin', f =>
    isNumber(f.raw) ? `${b(f.raw)} free credits added to your account.` : 'Free credits were added to your account.'),
  CREDIT_COSNUMED_FOR_JOBS: spec('Credits used for jobs', 'info', 'tablerCoin', f => {
    const count = clean(f.d.jobsCount);
    if (isNumber(f.raw) && isNumber(count)) return `${b(f.raw)} credits used for ${b(count)} ${plural(count, 'job', 'jobs')}.`;
    if (isNumber(f.raw)) return `${b(f.raw)} credits used for your jobs.`;
    return 'Credits were used for your jobs.';
  }),
  CREDIT_COSNUMED_FOR_CANDIDATES: spec('Credits used for candidates', 'info', 'tablerCoin', f => {
    const count = clean(f.d.candidatesCount);
    if (isNumber(f.raw) && isNumber(count)) return `${b(f.raw)} credits used for ${b(count)} ${plural(count, 'candidate', 'candidates')}.`;
    if (isNumber(f.raw)) return `${b(f.raw)} credits used for your candidates.`;
    return 'Credits were used for your candidates.';
  }),
  JOBS_DOWNGRADING_ALERT: spec('Jobs will be downgraded', 'warning', 'tablerAlertTriangle', f => {
    const count = clean(f.d.jobsDowngradedCount);
    return isNumber(count)
      ? `${b(count)} ${plural(count, 'job', 'jobs')} will be downgraded — upgrade period expired.`
      : 'Some jobs will be downgraded — upgrade period expired.';
  }),
  CANDIDATES_DOWNGRADING_ALERT: spec('Candidates will be downgraded', 'warning', 'tablerAlertTriangle', f => {
    const count = clean(f.d.candidatesDowngradedCount);
    return isNumber(count)
      ? `${b(count)} ${plural(count, 'candidate', 'candidates')} will be downgraded — upgrade period expired.`
      : 'Some candidates will be downgraded — upgrade period expired.';
  }),
  JOBS_DOWNGRADED: spec('Jobs downgraded', 'warning', 'tablerCircleArrowDown', f => {
    const count = clean(f.d.jobsDowngradedCount);
    return isNumber(count)
      ? `${b(count)} of your ${plural(count, 'jobs was', 'jobs were')} downgraded.`
      : 'Some of your jobs were downgraded.';
  }),
  CANDIDATES_DOWNGRADED: spec('Candidates downgraded', 'warning', 'tablerCircleArrowDown', f => {
    const count = clean(f.d.candidatesDowngradedCount);
    return isNumber(count)
      ? `${b(count)} of your ${plural(count, 'candidates was', 'candidates were')} downgraded.`
      : 'Some of your candidates were downgraded.';
  }),
  JOBS_INACTIVE_DUE_TO_LOW_CREDITS: spec('Jobs deactivated', 'warning', 'tablerAlertTriangle', f => {
    const count = clean(f.d.jobsCount);
    return isNumber(count)
      ? `${b(count)} ${plural(count, 'job', 'jobs')} deactivated due to low credits.`
      : 'Some jobs were deactivated due to low credits.';
  }),
  CANDIDATES_INACTIVE_DUE_TO_LOW_CREDITS: spec('Candidates deactivated', 'warning', 'tablerAlertTriangle', f => {
    const count = clean(f.d.candidatesCount);
    return isNumber(count)
      ? `${b(count)} ${plural(count, 'candidate', 'candidates')} deactivated due to low credits.`
      : 'Some candidates were deactivated due to low credits.';
  }),
  // Effective date = the end of a listing's active period (CreditService).
  JOBS_AND_CANDIDATES_DEACTIVATION_BY_EFFECTIVE_DATE: spec('Listings deactivated', 'warning', 'tablerAlertTriangle', f => {
    const counts = listingCounts(f.d.jobsDeactivatedCount, f.d.candidatesDeactivatedCount);
    return counts
      ? `${counts} deactivated — active period ended.`
      : 'Some listings were deactivated — active period ended.';
  }),
  JOBS_AND_CANDIDATES_DEACTIVATION_BY_EFFECTIVE_DATE_ALERT: spec('Listings ending soon', 'warning', 'tablerAlertTriangle', f => {
    const counts = listingCounts(f.d.jobsToBeDeactivatedCount, f.d.candidatesToBeDeactivatedCount);
    return counts ? `${counts} will be deactivated soon.` : 'Some listings will be deactivated soon.';
  }),
  USERTYPE_UPDATED: spec('Account type changed', 'info', 'tablerUser', f => {
    const userType = f.raw && !UUID_RE.test(f.raw) ? prettyStatus(f.raw) : '';
    if (f.actor && userType) return `${b(f.actor)} changed account type to ${b(userType)}.`;
    if (f.actor) return `${b(f.actor)} changed their account type.`;
    if (userType) return `A connection changed account type to ${b(userType)}.`;
    return 'A connection changed their account type.';
  }),
  MEMBERSHIP_UPDATED: spec('Plan upgraded', 'success', 'tablerCrown', f =>
    f.entity && !looksLikeId(f.entity)
      ? `Your account is upgraded to the ${b(f.entity)} plan.`
      : 'Your membership plan has been upgraded.'),
  UPDATED_TO_BUSINESS_PLAN: spec('Upgraded to business plan', 'success', 'tablerCrown', f =>
    `${pageName(f.business, 'Your account')} upgraded to a business plan.`),
  CUSTOMIZED_PLAN_REQUEST_UPDATED: spec('Plan request updated', 'info', 'tablerCrown', () =>
    'Your customised plan request was updated.'),
  PLAN_SUBSCRIPTION_IMPACTED: spec('Plan updated', 'info', 'tablerCrown', () =>
    'Your plan was updated. Contact support if anything looks wrong.'),
  PLAN_ACCESS_REVOKED_SOURCE_TRANSFER: spec('Plan access revoked', 'warning', 'tablerLock', f =>
    `Plan access was revoked${f.business ? ` for ${b(f.business)}` : ''}.`),
  PLAN_ACCESS_REVOKED_JOB_TARGET_RECEIVE: spec('Plan access revoked', 'warning', 'tablerLock', f =>
    `Plan access was revoked${f.business ? ` for ${b(f.business)}` : ''}.`),
  PLAN_ACCESS_REVOKED_CANDIDATE_TARGET_RECEIVE: spec('Plan access revoked', 'warning', 'tablerLock', f =>
    `Plan access was revoked${f.business ? ` for ${b(f.business)}` : ''}.`),
  PLAN_ACCESS_REVOKED_WORKSPACE_TARGET_RECEIVE: spec('Plan access revoked', 'warning', 'tablerLock', f =>
    `Plan access was revoked${f.business ? ` for ${b(f.business)}` : ''}.`),
  STRIPE_INVOICE_SUCCESS: spec('Payment successful', 'success', 'tablerCreditCard', f =>
    `Your ${planName(f.raw)} was renewed${onDate(f)}.`),
  STRIPE_INVOICE_FAILURE: spec('Payment failed', 'error', 'tablerCreditCard', f =>
    `Your ${planName(f.entity)} renewal failed${onDate(f)}. Check billing history.`),
  STRIPE_PAYMENT_TRANSACTION_FAILURE: spec('Payment failed', 'error', 'tablerCreditCard', f =>
    `Your ${planName(f.entity)} purchase failed${onDate(f)}. Check billing history.`),
  STRIPE_PAYMENT_TRANSACTION_SUCCESS: spec('Payment successful', 'success', 'tablerCreditCard', f =>
    `Your ${planName(f.entity)} purchase was successful.`),

  // ── Resume
  RESUME_REQUEST_INITIATED: spec('Resume requested', 'info', 'tablerFileText', resumeRequested),
  RESUME_REQUESTED: spec('Resume requested', 'info', 'tablerFileText', resumeRequested),
  RESUME_REQUEST_ACCEPTED: spec('Resume shared', 'success', 'tablerFileCheck', resumeUploaded),
  RESUME_UPDATED: spec('Resume updated', 'success', 'tablerFileCheck', resumeUploaded),

  // ── Admin — content moderation
  ADMIN_ACTION_ON_REPORT: spec('Report actioned', 'info', 'tablerFlag', reportActioned),
  ADMIN_ACTION_BY_REPORT: spec('Report actioned', 'info', 'tablerFlag', reportActioned),
  FLAG_RECEIVED_ACKNOWLEDGEMENT: spec('Report received', 'info', 'tablerFlag', f =>
    `The ${reportedItem(f)} you reported is under review by the admin.`),
};

// ── Fallbacks for types with no SPECS entry ─────────────────────────────────

/** Type name as a sentence-case title (`TEAM_BUDGET_EXCEEDED` → "Team budget
 *  exceeded"); "New notification" when that would be empty, an id, or too long
 *  for the toast title. */
function fallbackTitle(type: string): string {
  const humanized = humanizeType(type);
  if (!humanized || humanized === 'Notification' || looksLikeId(humanized) || humanized.length > 30) {
    return 'New notification';
  }
  return humanized.charAt(0) + humanized.slice(1).toLowerCase();
}

/** Human copy, not a code: rejects ids (`looksLikeId`) and single-word codes
 *  such as `RECRUITER`, `ON_HOLD`, `BLOCKED` that the `message` column holds. */
function isReadableText(v: string): boolean {
  return !!v && !looksLikeId(v) && /\s/.test(v) && /[a-z]/.test(v);
}

/** Body for unmapped types — always a sentence, never a bare name. */
function fallbackDescription(f: Fields): string {
  if (isReadableText(f.raw)) return esc(f.raw);
  if (f.actor && f.business) return `New update from ${b(f.actor)} at ${b(f.business)}.`;
  if (f.actor) return `New update from ${b(f.actor)}.`;
  if (f.business) return `New update from ${b(f.business)}.`;
  if (f.organisation) return `New update from ${b(f.organisation)}.`;

  // Final safety net: every toast must show something. No identifiers exposed.
  return 'You have a new notification.';
}

/** Colour + icon for unmapped types — the substring rules the nav bar used. */
function fallbackLook(type: string): { severity: NotifSeverity; icon: string } {
  if (type.includes('SELECTED') || type.includes('ACCEPTED')) return { severity: 'success', icon: 'tablerCircleCheck' };
  if (type.includes('REJECTED') || type.includes('REMOVED')) return { severity: 'error', icon: 'tablerCircleX' };
  if (type.includes('INVITE') || type.includes('REQUEST')) return { severity: 'info', icon: 'tablerUserPlus' };
  return { severity: 'info', icon: 'tablerBell' };
}

function categoryFor(type: string): NotifCategory {
  if (!type) return 'system';
  if (/AI|INSIGHT|SUMO/.test(type)) return 'ai';
  if (/ACCEPT|APPROVED|COMPLETED|CONFIRMED|PAID|PLACED|HIRED|SUCCESS|SELECTED|SHORTLISTED|JOINED|LIKED|VERIFIED|ACTIVATED|ONBOARDED/.test(type))
    return 'success';
  if (/REJECT|DECLINE|CANCEL|EXPIR|URGENT|FAIL|ALERT|WARN|WITHDRAW|OVERDUE|MISS|REQUIRED|REVOKE|DEACTIVATED|DELETED|REMOVED|INSUFFICIENT|IMPACTED/.test(type))
    return 'alert';
  return 'system';
}

function pickAvatarUrl(n: any): string | undefined {
  const d = n?.notifyData || {};
  const candidate = d.logo || d.photo || '';
  const trimmed = String(candidate).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  return trimmed;
}
